import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useAccessToken } from '@/features/auth/state/auth'

const BASE_URL = import.meta.env.VITE_API_BASE_URL
const api = axios.create({ baseURL: BASE_URL })

// Tipagem do DTO para criação da observação
export interface CreateObservationDto {
  mensage: string
  cnpj: string
  sender: string
  id_user?: string
  id_tenant?: string
  id_produto?: string
  isAlerta: boolean
  category: 'FINANCEIRO' | 'OPERACIONAL' | 'PADRAO' | 'CLIENTE'
}

// Hook para criar uma nova observação
export function useCreateObservation() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (dto: CreateObservationDto) => {
      if (!token) throw new Error('Usuário não autenticado')
      const response = await api.post('/observations', dto, {
        headers: { Authorization: `Bearer ${token}` }
      })

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['observation'] })
    }
  })
}

// Hook para buscar observações por ID de produto
export function useFetchObservationByProductId(id_produto: string | undefined) {
  const token = useAccessToken()

  return useQuery({
    queryKey: ['observation', 'produto', id_produto],
    queryFn: async () => {
      if (!id_produto) throw new Error('ID do produto não fornecido')

      const response = await api.get(`/observations/${id_produto}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      return response.data.data
    },
    enabled: Boolean(token && id_produto)
  })
}

// Hook para buscar observações por CNPJ do cliente
export function useFetchAllObservations(cnpj: string | undefined) {
  const token = useAccessToken()

  return useQuery({
    queryKey: ['observation', 'cliente', cnpj],
    queryFn: async () => {
      if (!cnpj) throw new Error('CNPJ não fornecido')

      const response = await api.get(`/observations/client/${cnpj}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      return response.data.data
    },
    enabled: Boolean(token && cnpj)
  })
}

// Hook para soft delete (atualiza isAtivo = false)
export function useSoftDeleteObservation() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (id: number | null) => {
      if (!token) throw new Error('Usuário não autenticado')
      if (!id) throw new Error('ID da observação não fornecido')

      const response = await api.post(
        `/observations/soft-delete/${id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['observation'] })
    }
  })
}

// Hook para deletar permanentemente uma observação (caso necessário)
export function useDeleteObservation() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (id: number) => {
      if (!token) throw new Error('Usuário não autenticado')

      const response = await api.delete(`/observations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['observation'] })
    }
  })
}
